"use client"
import React, { useEffect, useRef } from "react"

const NAV_SEARCH_PARAM = "search"
const RAINFOCUS_SEARCH_PARAM = "search-rainfocus"
const RESTORE_TIMEOUT_MS = 15_000

// Non-null while exposeNativeSearchParam has the shared term temporarily
// swapped into the native `?search=` slot. Lets the history wrappers below
// keep third-party echoes of that URL away from Next's router.
let activeNativeSwapTerm: string | null = null

export interface RainfocusWidgetProps {
  apiToken: string
  widgetId: string
  env?: "prod" | "staging" | "dev"
}

declare global {
  interface Window {
    jQuery?: any
    Rainfocus?: {
      Widget: new (config: {
        apiToken: string
        widgetId: string
        env: string
      }) => any
    }
    widget?: any
  }
}

/** Bypass Next/our history wrappers so temporary URL swaps don't notify React. */
const nativeReplaceUrl = (url: string) => {
  History.prototype.replaceState.call(
    window.history,
    window.history.state,
    "",
    url,
  )
}

const buildUrlWithSearchParam = (
  fromParam: string,
  toParam: string,
  term: string,
) => {
  const next = new URL(window.location.href)
  next.searchParams.delete(fromParam)
  next.searchParams.delete(toParam)
  next.searchParams.set(toParam, term)
  // Rainfocus parses the query string itself and treats "+" as a literal
  // character, so keep spaces as %20 instead of URLSearchParams' "+".
  const query = next.searchParams.toString().replace(/\+/g, "%20")
  return `${next.pathname}${query ? `?${query}` : ""}${next.hash}`
}

const normalizeSearchTerm = (term: string) =>
  term.trim().toLowerCase().replace(/\+/g, " ")

const catalogShowsSearchTerm = (root: HTMLElement, term: string) => {
  const normalized = normalizeSearchTerm(term)
  if (!normalized) return false
  const text = normalizeSearchTerm(root.innerText)
  return text.includes(`"${normalized}"`) || text.includes(`“${normalized}”`)
}

/**
 * Rainfocus only restores filters from its native `?search=` param. Shared
 * links use `?search-rainfocus=` (to avoid colliding with nav Algolia). Briefly
 * present the pasted term as `search` (via prototype replaceState so Next/nav
 * never see it), then restore `search-rainfocus` once the catalog has applied
 * the filter, on timeout, or on unmount.
 */
const exposeNativeSearchParam = (root: HTMLElement, signal: AbortSignal) => {
  const params = new URLSearchParams(window.location.search)
  const term = params.get(RAINFOCUS_SEARCH_PARAM)
  // Skip when empty, or when nav already owns `search` on the same URL.
  if (term === null || term === "" || params.has(NAV_SEARCH_PARAM)) return

  let restored = false
  const teardowns: Array<() => void> = []

  const restore = () => {
    if (restored) return
    restored = true
    activeNativeSwapTerm = null
    teardowns.forEach((teardown) => teardown())

    const current = new URLSearchParams(window.location.search)
    if (
      current.has(NAV_SEARCH_PARAM) &&
      !current.has(RAINFOCUS_SEARCH_PARAM)
    ) {
      nativeReplaceUrl(
        buildUrlWithSearchParam(NAV_SEARCH_PARAM, RAINFOCUS_SEARCH_PARAM, term),
      )
    }
  }

  activeNativeSwapTerm = term
  nativeReplaceUrl(
    buildUrlWithSearchParam(RAINFOCUS_SEARCH_PARAM, NAV_SEARCH_PARAM, term),
  )

  const observer = new MutationObserver(() => {
    if (catalogShowsSearchTerm(root, term)) restore()
  })
  observer.observe(root, {
    childList: true,
    subtree: true,
    characterData: true,
  })
  teardowns.push(() => observer.disconnect())

  const timeoutId = setTimeout(restore, RESTORE_TIMEOUT_MS)
  teardowns.push(() => clearTimeout(timeoutId))

  signal.addEventListener("abort", restore, { once: true })

  // Chip may already be present if the catalog restored instantly.
  if (catalogShowsSearchTerm(root, term)) restore()
}

/**
 * `search` belongs to the nav's Algolia search, not the catalog, but
 * Rainfocus reads `location.search` asynchronously while booting (not
 * synchronously inside its constructor, and not at a single fixed point we
 * can reliably preempt — other app code keeps re-asserting the URL in that
 * same window) and silently adopts it as its own filter term. So whatever's
 * typed into the nav search reappears in the catalog too, but only on a
 * fresh load/refresh, since a client-side nav edit to `search` doesn't
 * re-trigger Rainfocus's init.
 *
 * Rather than race Rainfocus's init to hide the param in time, let it apply
 * the term, then clear just the search filter via Rainfocus's own
 * "Clear search" control once the chip appears, correcting the URL back to
 * the nav's value afterward (Rainfocus's own click handler writes its own,
 * unwanted URL update as a side effect — letting that write actually land
 * and fixing it up after is more reliable than swallowing it outright:
 * Rainfocus's handler appears to read `location.search` back to confirm its
 * own write, so silently no-op'ing it confuses that internal sync and made
 * the clear not stick more often in testing).
 */
const CLEAR_SEARCH_SELECTOR = ".clear-search"
// How long to keep correcting the URL after clicking "Clear search":
// Rainfocus's own (rename-wrapped) URL update can land more than once and
// more than one tick later, so a single corrective write can itself get
// overwritten by a later one. Poll-and-correct for a short window instead.
const URL_RESTORE_WINDOW_MS = 2_000
const URL_RESTORE_POLL_MS = 100
// Rainfocus's chip can flash onto the DOM slightly before its own filter
// state has actually settled; clicking "Clear search" during that window
// doesn't stick (a follow-up render re-applies the term moments later).
// Wait for the chip to stay put before clearing it.
const SETTLE_DELAY_MS = 400

const clearNavOwnedSearchFromCatalog = (root: HTMLElement, signal: AbortSignal) => {
  // exposeNativeSearchParam already owns `search` for a pasted
  // search-rainfocus link; let the catalog read that term as intended.
  if (activeNativeSwapTerm !== null) return

  const term = new URLSearchParams(window.location.search).get(NAV_SEARCH_PARAM)
  if (!term) return

  let stopped = false
  // True whenever we're mid settle-delay or mid-click/restore, so we don't
  // double-click while a previous clear is still in flight. Rainfocus can
  // re-apply the term a second time after an initial clear (e.g. once its
  // results fetch resolves), so this re-arms rather than firing once.
  let busy = false
  const teardowns: Array<() => void> = []

  const stop = () => {
    if (stopped) return
    stopped = true
    teardowns.forEach((teardown) => teardown())
  }

  const clickClear = () => {
    const clearButton = root.querySelector<HTMLButtonElement>(
      CLEAR_SEARCH_SELECTOR,
    )
    if (!clearButton) {
      busy = false
      return
    }

    // Don't freeze window.history here: it's shared with unrelated app code
    // (e.g. useAuthCheck's OAuth cleanup) that may need to write during this
    // window, and blanket-blocking it silently drops those writes. Instead,
    // let Rainfocus's click handler do whatever it wants, then keep forcing
    // the nav's URL back for a short window via History.prototype (bypassing
    // Next, same as nativeReplaceUrl elsewhere in this file) — its own URL
    // update can land more than once.
    const desiredUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
    const currentUrl = () =>
      `${window.location.pathname}${window.location.search}${window.location.hash}`
    clearButton.click()
    let elapsed = 0
    const pollId = setInterval(() => {
      if (currentUrl() !== desiredUrl) nativeReplaceUrl(desiredUrl)
      elapsed += URL_RESTORE_POLL_MS
      if (elapsed >= URL_RESTORE_WINDOW_MS) {
        clearInterval(pollId)
        busy = false
      }
    }, URL_RESTORE_POLL_MS)
    teardowns.push(() => clearInterval(pollId))
  }

  const maybeClear = () => {
    if (stopped || busy || !catalogShowsSearchTerm(root, term)) return
    busy = true
    const settleId = setTimeout(() => {
      if (stopped) return
      if (catalogShowsSearchTerm(root, term)) {
        clickClear()
      } else {
        busy = false
      }
    }, SETTLE_DELAY_MS)
    teardowns.push(() => clearTimeout(settleId))
  }

  const observer = new MutationObserver(maybeClear)
  observer.observe(root, {
    childList: true,
    subtree: true,
    characterData: true,
  })
  teardowns.push(() => observer.disconnect())

  const timeoutId = setTimeout(stop, RESTORE_TIMEOUT_MS)
  teardowns.push(() => clearTimeout(timeoutId))

  signal.addEventListener("abort", stop, { once: true })

  // Chip may already be present if the catalog applied it instantly.
  maybeClear()
}

export const RainfocusWidget: React.FC<RainfocusWidgetProps> = ({
  apiToken,
  widgetId,
  env = "prod",
}) => {
  const widgetRef = useRef<HTMLDivElement>(null)
  const scriptsLoadedRef = useRef(false)

  // The catalog syncs its filter term to `?search=`, the same param the site
  // nav deep-links its Algolia search with, so typing here used to open the
  // nav's search overlay. Rename the catalog's writes to keep them separate.
  useEffect(() => {
    // Scoped to the bundle's own calls so the nav keeps deep-linking `search`.
    const fromRainfocus = () => (new Error().stack ?? "").includes("rainfocus")

    const rename = (url: Parameters<History["pushState"]>[2]) => {
      if (url == null || !fromRainfocus()) return url
      const next = new URL(String(url), window.location.href)
      const term = next.searchParams.get(NAV_SEARCH_PARAM)
      if (term === null) return url
      next.searchParams.delete(NAV_SEARCH_PARAM)
      next.searchParams.set(RAINFOCUS_SEARCH_PARAM, term)
      return `${next.pathname}${next.search}${next.hash}`
    }

    // While exposeNativeSearchParam has the term swapped into `?search=`,
    // unrelated code that rebuilds the URL from window.location (e.g.
    // useAuthCheck's OAuth cleanup) can echo that temporary param through
    // window.history. Next's patched replaceState would read it and sync
    // `search=` into useSearchParams, deploying the nav's search overlay —
    // which then wipes the URL. Detect such echoes so they can be routed
    // past Next via the History prototype.
    const echoesSwappedTerm = (url: Parameters<History["pushState"]>[2]) => {
      if (activeNativeSwapTerm === null || url == null) return false
      try {
        return (
          new URL(String(url), window.location.href).searchParams.get(
            NAV_SEARCH_PARAM,
          ) === activeNativeSwapTerm
        )
      } catch {
        return false
      }
    }

    // Next's router patches window.history.pushState/replaceState in its own
    // mount effect (on the root AppRouter) to keep useSearchParams() synced,
    // and it reads the raw url to do that *before* delegating to whatever
    // pushState/replaceState it finds already installed. React runs effects
    // child-before-parent, so if we installed our wrapper synchronously here
    // it would end up *underneath* Next's: Next would read the un-renamed
    // `search=` value and sync useSearchParams() to it (opening the nav's
    // overlay) before our rename ever ran. Deferring to a macrotask lets
    // Next's effect finish first, so our wrapper installs on top and Next
    // only ever observes the already-renamed url.
    let applied = false
    let pushState = window.history.pushState
    let replaceState = window.history.replaceState

    const timeoutId = window.setTimeout(() => {
      ;({ pushState, replaceState } = window.history)
      window.history.pushState = function (state, title, url) {
        if (echoesSwappedTerm(url) && !fromRainfocus()) {
          return History.prototype.pushState.call(this, state, title, url)
        }
        return pushState.call(this, state, title, rename(url))
      }
      window.history.replaceState = function (state, title, url) {
        if (echoesSwappedTerm(url) && !fromRainfocus()) {
          return History.prototype.replaceState.call(this, state, title, url)
        }
        return replaceState.call(this, state, title, rename(url))
      }
      applied = true
    }, 0)

    return () => {
      window.clearTimeout(timeoutId)
      if (applied) {
        window.history.pushState = pushState
        window.history.replaceState = replaceState
      }
    }
  }, [])

  useEffect(() => {
    const hydrateAbort = new AbortController()

    const loadRainfocusResources = async () => {
      if (scriptsLoadedRef.current) return

      try {
        // Load jQuery/ CSS Files // Rainfocus Bundle
        if (!window.jQuery) {
          await loadScript(
            "https://ajax.googleapis.com/ajax/libs/jquery/3.6.1/jquery.min.js",
          )
        }

        await loadCSS(
          "https://static.rainfocus.com/gfonts/css/e47fce46a9d2402094879d2f861fb21e.css",
        )
        await loadCSS(
          "https://events.rainfocus.com/scripts/catalog/bundle.min.css",
        )
        await loadCSS(
          "https://events.rainfocus.com/assets/css/rainfocusWidgets.css",
        )

        await loadScript("https://events.rainfocus.com/js/bundle.js")

        if (hydrateAbort.signal.aborted) return
        scriptsLoadedRef.current = true
        initializeWidget()
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        // console.error('Failed to load Rainfocus resources:', error)
      }
    }

    const loadScript = (src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(
          `script[src="${src}"]`,
        )
        if (existing) {
          // The tag may still be loading (e.g. StrictMode's second effect
          // run finds the first run's tag); wait for it instead of resolving
          // early, or initializeWidget runs before window.Rainfocus exists.
          if (existing.dataset.loaded === "true") {
            resolve()
            return
          }
          existing.addEventListener("load", () => resolve(), { once: true })
          existing.addEventListener(
            "error",
            () => reject(new Error(`Failed to load script: ${src}`)),
            { once: true },
          )
          return
        }

        const script = document.createElement("script")
        script.src = src
        script.onload = () => {
          script.dataset.loaded = "true"
          resolve()
        }
        script.onerror = () =>
          reject(new Error(`Failed to load script: ${src}`))
        document.head.appendChild(script)
      })
    }

    const loadCSS = (href: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector<HTMLLinkElement>(
          `link[href="${href}"]`,
        )
        if (existing) {
          if (existing.dataset.loaded === "true") {
            resolve()
            return
          }
          existing.addEventListener("load", () => resolve(), { once: true })
          existing.addEventListener(
            "error",
            () => reject(new Error(`Failed to load CSS: ${href}`)),
            { once: true },
          )
          return
        }

        const link = document.createElement("link")
        link.rel = "stylesheet"
        link.href = href
        link.onload = () => {
          link.dataset.loaded = "true"
          resolve()
        }
        link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`))
        document.head.appendChild(link)
      })
    }

    const initializeWidget = () => {
      const Rainfocus = window.Rainfocus
      const root = widgetRef.current
      if (Rainfocus && root) {
        try {
          exposeNativeSearchParam(root, hydrateAbort.signal)
          window.widget = new Rainfocus.Widget({
            apiToken,
            widgetId,
            env,
          })
          clearNavOwnedSearchFromCatalog(root, hydrateAbort.signal)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          // console.error('Failed to initialize Rainfocus widget:', error)
        }
      }
    }

    loadRainfocusResources()

    // Cleanup function
    return () => {
      hydrateAbort.abort()
      if (window.widget) {
        // Clean up widget if needed
        window.widget = undefined
      }
    }
  }, [apiToken, widgetId, env])

  return <div ref={widgetRef} className="rf-widget" id="rf-catalog" />
}
